/** Content-level links reuse real pages; they never translate one business ID into another. */
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  NAVIGATION_GROUPS,
  resolveNavigationOwner,
  navigationReturnDestination,
} from "./moduleNav";
import {
  QuoteSettingsLink,
  QuoteJourneyReturn,
} from "@/components/quote/journey/QuoteJourneyLinks";
import { canOpenQuoteSettings } from "@/utils/quoteSettingsRoles";
import "./PageOwnership.css";

export default function PageOwnership() {
  const location = useLocation();
  const { user } = useAuth();
  const owner = resolveNavigationOwner(location.pathname);
  if (!owner) return null;
  const currentUrl = location.pathname + location.search + location.hash;
  const admin = user?.role === "platform_ops" || user?.role === "it_admin";
  const legacy = !location.pathname.startsWith("/app/");
  const group = NAVIGATION_GROUPS.find((g) => g.id === owner.group);
  const links: { label: string; to: string; current?: boolean }[] = [];

  if (legacy) {
    // The resource/configuration pages remain in their original guarded shell.
    const target = navigationReturnDestination(location.pathname);
    const label =
      owner.id === "tools"
        ? "设备与资源"
        : owner.id === "gateway"
          ? "数据质量与接入状态"
          : owner.label;
    links.push({ label: `返回${label}`, to: target });
  }
  if (owner.id === "quotes") {
    // QuoteProject is an assembly quote, never a business Project.
    const view: "machining" | "bid" | "line" =
      location.pathname === "/app/quote"
        ? "bid"
        : location.pathname.startsWith("/cnc/line-quotes")
          ? "line"
          : "machining";
    links.push(
      {
        label: "加工报价",
        to: view === "machining" ? currentUrl : "/app/quickquote",
        current: view === "machining",
      },
      {
        label: "商务投标",
        to: view === "bid" ? currentUrl : "/app/quote",
        current: view === "bid",
      },
      {
        label: "整线报价（其他报价工具）",
        to: view === "line" ? currentUrl : "/cnc/line-quotes",
        current: view === "line",
      },
      { label: "报价设置", to: "/app/quickquote/settings/rates" },
    );
    if (canOpenQuoteSettings(user?.role))
      links.push(
        { label: "报价要素目录", to: "/app/quickquote/settings/catalogs" },
        { label: "本厂报价标定", to: "/app/quickquote/settings/calibration" },
      );
  }
  if (owner.id === "parts") {
    if (!legacy && location.pathname !== "/app/mech")
      links.push({ label: "返回零件与图纸", to: "/app/mech" });
    links.push(
      { label: "生成模型", to: "/cnc/generate-model" },
      { label: "参数化改图", to: "/cnc/redesign" },
      { label: "CAD 拆图", to: "/cnc/drawing" },
      { label: "批量出图", to: "/cnc/drawing-batch" },
      { label: "生成出图确认", to: "/app/drawing" },
    );
    if (admin) links.push({ label: "零件管理", to: "/admin/parts" });
  }
  if (owner.id === "projects" && admin)
    links.push(
      { label: "客户管理", to: "/admin/customers" },
      { label: "项目管理", to: "/admin/projects" },
    );
  if (owner.id === "tools")
    links.push(
      {
        label: "台账",
        to: "/admin/tool-images",
        current: location.pathname === "/admin/tool-images",
      },
      {
        label: "待补全",
        to: "/admin/tools",
        current: location.pathname === "/admin/tools",
      },
    );
  if (
    !legacy &&
    owner.id === "capabilities" &&
    location.pathname !== "/app/caps"
  )
    links.push({ label: "返回技能与工具", to: "/app/caps" });
  if (!links.length) return null;
  return (
    <nav className="wd-owner-nav" aria-label="页面归属与工具">
      <span>
        {group?.label} / {owner.label}
      </span>
      {links.map((link) => {
        if (
          link.label === "加工报价" &&
          location.pathname.startsWith("/app/quickquote/settings/")
        )
          return <QuoteJourneyReturn key={link.label} />;
        const OwnerLink = link.to.startsWith("/app/quickquote/settings/")
          ? QuoteSettingsLink
          : Link;
        return (
          <OwnerLink
            key={link.label}
            to={link.current ? currentUrl : link.to}
            aria-current={link.current ? "page" : undefined}
          >
            {link.label}
          </OwnerLink>
        );
      })}
    </nav>
  );
}
